<!--
  Tax Assign Listings Modal - for assigning a tax/fee to multiple listings
  2025-11-02: Created for bulk listing assignment feature
-->
<template>
  <v-card class="tax-assign-listings-modal" max-width="600">
    <!-- Header -->
    <v-card-title class="pa-6 primary--text">
      <h2 class="text-h5 font-weight-medium">
        {{ $t('Assign to Listings') }}
      </h2>
      <v-spacer />
      <v-btn icon @click="close">
        <v-icon>close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider />

    <v-form ref="assignForm" lazy-validation @submit.prevent="assignTax">
      <v-card-text class="pa-6">
        <!-- Tax/Fee Information -->
        <v-alert type="info" text dense class="mb-4">
          <div class="d-flex align-center">
            <div>
              <div class="font-weight-medium">
                {{ tax.name || tax.tax_type }}
              </div>
              <div class="text-caption">
                {{ getAmountDisplay(tax) }}
              </div>
            </div>
          </div>
        </v-alert>

        <!-- Listings Picker -->
        <multi-property-picker
          v-model="selectedListingIds"
          :label="$t('Select Listings')"
          :rules="[
            v =>
              (v && v.length > 0) || $t('Please select at least one listing'),
          ]"
        />

        <div class="text-caption grey--text mt-2">
          {{
            $t(
              'The tax/fee will be assigned to selected listings with the default amount. You can customize amounts per listing later.'
            )
          }}
        </div>
      </v-card-text>

      <!-- Footer Actions -->
      <v-divider />
      <v-card-actions class="pa-4 justify-end">
        <v-btn text class="px-6" @click="close">
          {{ $t('Cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          depressed
          type="submit"
          class="px-6"
          :loading="assigning"
        >
          {{ $t('Assign') }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import MultiPropertyPicker from 'components/multi-property-picker.vue'
import axios from 'axios'
import Toaster from '@/utils/toaster'
import FormattersMixin from 'components/mixins/formatters-mixin'

export default {
  name: 'TaxAssignListingsModal',
  components: {
    MultiPropertyPicker,
  },
  mixins: [FormattersMixin],
  props: {
    tax: {
      type: Object,
      required: true,
    },
    onChange: {
      type: Function,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedListingIds: [],
      assigning: false,
    }
  },
  mounted() {
    // Prepopulate with existing assigned listings
    if (this.tax.listing_taxes && this.tax.listing_taxes.length > 0) {
      this.selectedListingIds = this.tax.listing_taxes.map(lt => lt.listing_id)
    }
  },
  methods: {
    close() {
      this.$store.commit('hideModal')
    },
    getAmountDisplay(tax) {
      if (tax.units === 'listing_based') {
        return this.$t('Amount from listing')
      } else if (tax.units === 'formula') {
        return `${this.$t('Formula')}: ${tax.formula}`
      } else if (tax.units === 'percentage') {
        return `${tax.amount}%`
      } else {
        return this.toMoney(tax.amount)
      }
    },
    async assignTax() {
      if (!this.$refs.assignForm.validate()) {
        return
      }

      this.assigning = true
      try {
        await axios.post(`/api/taxes/${this.tax.id}/assign_to_listings`, {
          listing_ids: this.selectedListingIds,
        })
        this.onChange()
        Toaster.show([
          {
            type: 'success',
            text: this.$t(
              `Tax/fee assigned to ${this.selectedListingIds.length}  listings successfully`
            ),
          },
        ])
        this.close()
      } catch (error) {
        console.error('Failed to assign tax to listings:', error)
        const errorMessage =
          error.response?.data?.errors?.join(', ') ||
          this.$t('Failed to assign tax to listings')
        Toaster.show([{ type: 'error', text: errorMessage }])
      } finally {
        this.assigning = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tax-assign-listings-modal {
  border-radius: 8px;
}
</style>
